// De keuzelijst 'Kies een huishouden' op de kaart 'Adressen om te koppelen'.
//
// Nodig omdat de kaart eerst `Title || ID` toonde: oudere huishoudens zonder
// titel stonden er als uuid in (testronde 2026-10-12, B4), en een huishouden
// van een adres dat niet in het register staat heeft geen BAG-id, dus het werd
// bij een tweede invulling op hetzelfde adres niet herkend (B3). Dan klikt
// iemand op 'Nieuw huishouden' en staat het adres er twee keer.
//
// Herkennen op het getypte adres is hier een VOORSTEL, geen koppeling: het
// adres is niet geverifieerd, dus de organisatie kiest nog steeds zelf.
// AI-CONTEXT: See docs/features/adres-koppeling-algemene-qr.md

import { buildStreetLabelMap } from '../adressHelpers/buildStreetLabels'

/** De PersonaData-titels die deze keuzelijst leest. */
export const HOUSEHOLD_CHOICE_FIELDS = ['Straatnaam', 'Huisnummer', 'Toevoeging', 'Postcode']

/**
 * @typedef {object} HouseholdChoice
 * @property {string} id - Adresses.ID
 * @property {string} label - Naam in de keuzelijst
 * @property {boolean} sameAddress - Zelfde BAG-id of zelfde getypte adres als het geval
 */

const norm = (value) => String(value ?? '').toUpperCase().replace(/\s+/g, '')

/**
 * Postcode + huisnummer + toevoeging als één sleutel. De toevoeging wordt aan
 * het huisnummer geplakt: '12' + 'A' en '12A' + '' zijn hetzelfde adres.
 * @param {{Postcode?: string, Huisnummer?: string, Toevoeging?: string}} [parts]
 * @returns {string} '' als postcode of huisnummer ontbreekt
 */
const addressKey = (parts) => {
    const postcode = norm(parts?.Postcode)
    const nummer = norm(parts?.Huisnummer)
    if (!postcode || !nummer) return ''
    return `${postcode}|${nummer}${norm(parts?.Toevoeging)}`
}

/**
 * @param {{ID: string, Title?: string}} household
 * @param {Record<string, string>} fields
 * @returns {string}
 */
const labelFor = (household, fields) => {
    const nummer = `${String(fields.Huisnummer || '').trim()}${String(fields.Toevoeging || '').trim().toUpperCase()}`
    if (fields.Straatnaam) return `${fields.Straatnaam} ${nummer}`.trim()
    if (fields.Postcode && nummer) return `${norm(fields.Postcode)} ${nummer}`
    return String(household.Title || '').trim() || 'Naamloos huishouden'
}

/**
 * @param {Array<{ID: string, Title?: string, Location?: {BAGid?: string}}>} households
 * @param {Array<object>|string} personaData - Rijen met een titel uit HOUSEHOLD_CHOICE_FIELDS
 * @param {{BAGid?: string, Address?: object}} match - Het AddressMatches-document
 * @returns {{options: HouseholdChoice[], suggested: string}} `suggested` = het enige huishouden op hetzelfde adres, anders ''
 */
const householdChoices = (households, personaData, match) => {
    // Straat, nummer, toevoeging en postcode zijn vaste gegevens op het
    // huishouden; tot de migratie vullen de oude regels aan (T9).
    // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md
    const fields = buildStreetLabelMap(Array.isArray(personaData) ? personaData : [], households)
    const matchKey = addressKey(match?.Address)
    const collator = new Intl.Collator('nl', { numeric: true })

    const options = (households || []).map((h) => {
        const own = fields[h.ID] || {}
        const sameBag = Boolean(match?.BAGid) && h.Location?.BAGid === match.BAGid
        const sameTyped = Boolean(matchKey) && addressKey(own) === matchKey
        return { id: h.ID, label: labelFor(h, own), sameAddress: sameBag || sameTyped }
    }).sort((a, b) => Number(b.sameAddress) - Number(a.sameAddress) || collator.compare(a.label, b.label))

    const same = options.filter((o) => o.sameAddress)
    return { options, suggested: same.length === 1 ? same[0].id : '' }
}

export default householdChoices
